import type {
  CasePrepUscisFilledForm,
  CasePrepUscisSource,
  CasePrepUscisSourceType
} from "./types";

export { BRAND_BLUE } from "@/assets/brand/colors";
// Must stay in sync with MAX_CASE_EXTEND_FORM_SOURCES on the backend.
export const MAX_FORM_SOURCES = 10;

export function stringifyValue(value: unknown): string {
  if (typeof value === "boolean") return value ? "true" : "false";
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return JSON.stringify(value);
}

// Mirrors the backend `_has_extend_filled_value`: null/undefined and
// whitespace-only strings are empty; false, 0, and non-empty collections count.
function hasFilledValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.some(hasFilledValue);
  if (typeof value === "object") {
    return Object.values(value as Record<string, unknown>).some(hasFilledValue);
  }
  return true;
}

/**
 * How many fields on a form currently hold an answer.
 *
 * Merges `field_values` (waterfall output) with `edited_field_values` (manual
 * edits win, matching how the overlay resolves a field), then counts the fields
 * whose resolved value is non-empty. A field cleared to `""` resolves empty and
 * is not counted, since there is nothing visible to discard.
 */
export function countAnsweredFields(form: CasePrepUscisFilledForm): number {
  const resolved = new Map<string, unknown>();
  for (const [key, value] of Object.entries(form.field_values ?? {})) {
    resolved.set(key, value);
  }
  for (const [key, value] of Object.entries(form.edited_field_values ?? {})) {
    resolved.set(key, value);
  }
  let count = 0;
  resolved.forEach((value) => {
    if (hasFilledValue(value)) count += 1;
  });
  return count;
}

export function sourceTypeLabel(type: CasePrepUscisSourceType): string {
  if (type === "case_file") return "Case File";
  if (type === "evidence_document") return "Evidence";
  if (type === "intake") return "Intake";
  if (type === "attorney_profile") return "Attorney Profile";
  return "Pasted Text";
}

export function sourceStepLeadIn(index: number, total: number): string {
  if (index === 0) return "First, use";
  if (index === total - 1) return "Finally, use";
  return "Then, use";
}

export function sourceCardLabel(source: CasePrepUscisSource): string {
  if (source.source_type !== "free_text") return source.source_label;

  const text = String(source.free_text || source.source_label || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return source.source_label;

  const maxLength = 96;
  const preview =
    text.length > maxLength ? `${text.slice(0, maxLength).trimEnd()}...` : text;
  return `"${preview}"`;
}

export function sourceDetailLabel(source: CasePrepUscisSource): string {
  if (source.source_type !== "free_text") return source.source_label;

  const text = String(source.free_text || source.source_label || "").trim();
  if (!text) return source.source_label;
  return `"${text}"`;
}

export function sourceRecommendationReason(
  source: CasePrepUscisSource
): string {
  const raw = source.metadata?.recommendation_reason;
  return typeof raw === "string" ? raw.trim() : "";
}

export function isRecommendedSource(source: CasePrepUscisSource): boolean {
  return source.metadata?.recommended === true;
}

export function sourceRenderKey(source: CasePrepUscisSource): string {
  const metadata = source.metadata ?? {};
  return [
    source.source_type,
    source.source_id ?? "",
    metadata.outgoing_intake_submission_id ?? "",
    source.source_label,
    source.free_text ?? ""
  ].join(":");
}

export function reviewStatusLabel(status?: string): string {
  const normalized = String(status || "").trim();
  if (!normalized) return "";
  return normalized
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function reviewStatusBadgeClassName(status?: string): string {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  if (normalized === "accepted") {
    return "border-success-100 bg-success-100 text-success-800";
  }
  if (normalized === "revision_requested") {
    return "border-warning-100 bg-warning-100 text-warning-800";
  }
  return "border-line-2 bg-fill-1 text-fg";
}
